<template>
	<div class="touch-card-list">
		<v-expansion-panels v-model="openKeys" multiple flat class="touch-card-list__panels">
			<v-expansion-panel v-for="(row, index) in visibleItems" :key="getRowKey(row, index)" :value="getRowKey(row, index)" :class="['touch-card-list__panel', rowClassField ? row[rowClassField] : null]">
				<v-expansion-panel-title expand-icon="keyboard_arrow_down" collapse-icon="keyboard_arrow_up" :readonly="!hasDetailColumns" :hide-actions="!hasDetailColumns">
					<div class="touch-card-list__header">
						<div class="touch-card-list__heading" :class="{ 'touch-card-list__heading--actionable': hasDetailColumns }">
							<ProductCellRenderer v-if="titleImageField || titleTagsField" class="touch-card-list__product" :params="productParams(row)" />
							<template v-else>
								<div class="title">{{ title(row) }}</div>
								<div v-if="subtitle(row)" class="subtitle">{{ subtitle(row) }}</div>
							</template>
						</div>
						<TouchCardMetrics :row="row" :columns="columns" :metric-layout="metricLayout" :expanded="isExpanded(getRowKey(row, index))" @cell-change="emit('cell-change', $event)" />
					</div>
				</v-expansion-panel-title>
			</v-expansion-panel>
		</v-expansion-panels>

		<div v-if="hasMore" ref="sentinelRef" class="touch-card-list__sentinel">
			<v-progress-circular indeterminate size="20" width="2" color="primary" />
		</div>
	</div>
</template>

<script setup>
import { ref, computed } from 'vue';
import TouchCardMetrics from '@/components/agGrid/TouchCardMetrics.vue';
import ProductCellRenderer from '@/components/agGrid/ProductCellRenderer.vue';
import { useProgressiveList } from '@/composables/useProgressiveList';

const props = defineProps({
	rows: { type: Array, default: () => [] },
	columns: { type: Array, default: () => [] },
	titleFields: { type: Array, default: () => [] },
	subtitleFields: { type: Array, default: () => [] },
	titleImageField: { type: String, default: null },
	titleTagsField: { type: String, default: null },
	rowKey: { type: String, default: 'id' },
	metricLayout: { type: String, default: 'grid' },
	chunkSize: { type: Number, default: 40 },
	rowClassField: { type: String, default: null },
});

const emit = defineEmits(['cell-change']);

const { sentinelRef, visibleItems, hasMore } = useProgressiveList(
	() => props.rows,
	() => props.chunkSize
);

// Every card is its own expansion panel, same mechanism as TouchCardGroup's headers: detail columns
// (reference/total-references counts) stay inside the title and TouchCardMetrics itself only reveals them
// once this row's key is in openKeys. Callers with no detail columns at all (e.g. SpsBuilder) get a
// read-only card with no chevron, since there's nothing to fold out.
const hasDetailColumns = computed(() => props.columns.some((column) => column.detail));

const openKeys = ref([]);
function isExpanded(key) {
	return openKeys.value.includes(key);
}

function getValue(row, field) {
	return row && field ? (row[field] ?? '') : '';
}

function getRowKey(row, index) {
	return row?.[props.rowKey] ?? index;
}

function title(row) {
	const field = props.titleFields.find((candidate) => getValue(row, candidate) !== '');
	return field ? getValue(row, field) : '';
}

// Callers asking only for title tags (no product image) still go through ProductCellRenderer rather than a
// second copy of the chip markup - `icon: false` drops its avatar, leaving the plain title/subtitle layout.
function productParams(row) {
	return {
		value: title(row),
		subtitle: subtitle(row),
		imageThumbnailUid: props.titleImageField ? row[props.titleImageField] : null,
		icon: props.titleImageField ? undefined : false,
		tags: props.titleTagsField ? row[props.titleTagsField] : null,
	};
}

function subtitle(row) {
	return props.subtitleFields
		.map((field) => getValue(row, field))
		.filter((value) => value !== '' && value !== null && value !== undefined)
		.join(' · ');
}
</script>

<style scoped lang="scss">
@use '@/styles/variables' as *;

.touch-card-list {
	display: flex;
	flex-direction: column;
	gap: 8px;

	&__panel {
		border: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
		border-radius: 12px;
		background: rgb(var(--v-theme-surface));
		box-shadow: none;
		animation: touch-card-fade-in 0.28s ease both;

		&:first-child {
			margin-top: 8px;
		}

		&::after {
			display: none;
		}

		// Same top-right chevron treatment as touch-card-group__panel, so a group's title and the store
		// cards nested under it read as the same kind of panel.
		:deep(> .v-expansion-panel-title) {
			padding: 10px 12px;
			min-height: 0;
			gap: 8px;
			border-radius: 10px;
			position: relative;
		}

		:deep(.v-expansion-panel-title__icon) {
			position: absolute;
			top: 10px;
			right: 12px;
			margin: 0;
		}
	}

	&__header {
		display: flex;
		flex-direction: column;
		gap: 2px;
		width: 100%;
		min-width: 0;
	}

	&__heading {
		display: flex;
		flex-direction: column;
		min-width: 0;

		// Only rows with a chevron (detail columns to reveal) need to leave it room; a plain, non-actionable
		// row can use the panel's full width for its title.
		&--actionable {
			padding-right: 28px;
		}
	}

	.title {
		font-size: $font-size;
		line-height: $font-size-extra-large;
		font-weight: 600;
		white-space: nowrap;
		text-overflow: ellipsis;
		overflow: hidden;
	}

	.subtitle {
		font-size: 12px;
		line-height: $font-size;
		color: rgba(var(--v-theme-on-surface), 0.6);
		white-space: nowrap;
		text-overflow: ellipsis;
		overflow: hidden;
	}

	&__product {
		width: 100%;

		:deep(.product-cell-renderer__name) {
			font-size: $font-size;
			line-height: $font-size-extra-large;
		}

		:deep(.product-cell-renderer__subtitle) {
			font-size: 12px;
			line-height: $font-size;
		}
	}

	&__sentinel {
		display: flex;
		align-items: center;
		justify-content: center;
		min-height: 40px;
	}
}

@keyframes touch-card-fade-in {
	from {
		opacity: 0;
		transform: translateY(8px);
	}

	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@media (prefers-reduced-motion: reduce) {
	.touch-card-list__panel {
		animation: none;
	}
}
</style>
